<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeModal"></div>
    
    <!-- Modal -->
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="relative bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 class="text-xl font-semibold text-gray-900">AGS Filters</h2>
          <button 
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 flex overflow-hidden">
          <!-- Left Panel: Categories -->
          <div class="w-80 bg-white border-r border-gray-200 overflow-y-auto">
            <div class="p-4">
              <h3 class="text-sm font-semibold text-gray-700 mb-3">Categories</h3>
              
              <!-- Loading state -->
              <div v-if="loadingCategories" class="flex items-center justify-center py-8">
                <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span class="ml-2 text-sm text-gray-500">Loading categories...</span>
              </div>
              
              <!-- Categories list -->
              <div v-else class="space-y-2">
                <div
                  v-for="category in availableCategories"
                  :key="category.category_name"
                  @click="selectCategory(category)"
                  :class="[
                    'cursor-pointer px-3 py-3 rounded-md text-sm transition-colors border',
                    selectedCategory?.category_name === category.category_name
                      ? 'bg-blue-50 text-blue-700 border-blue-200'
                      : 'text-gray-700 hover:bg-gray-50 border-transparent hover:border-gray-200'
                  ]"
                >
                  <div class="font-medium">{{ category.display_name }}</div>
                  <div class="text-xs text-gray-500 mt-1">{{ category.description }}</div>
                  
                  <!-- Geographic Level Badges -->
                  <div class="flex items-center gap-1 mt-2">
                    <span class="text-xs text-gray-400 mr-1">Available for:</span>
                    <span
                      v-for="level in category.geographic_levels"
                      :key="level"
                      class="px-1.5 py-0.5 text-xs rounded font-medium bg-blue-100 text-blue-800"
                    >
                      {{ formatGeographicLevel(level) }}
                    </span>
                  </div>
                  
                  <!-- Data Tier Badges -->
                  <div class="flex items-center gap-1 mt-1">
                    <span
                      v-for="tier in category.data_tiers"
                      :key="tier"
                      :class="[
                        'px-1.5 py-0.5 text-xs rounded font-medium',
                        tier === 'premium' 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-green-100 text-green-800'
                      ]"
                    >
                      {{ tier }}
                    </span>
                  </div>
                </div>
              </div>
              
              <!-- No categories message -->
              <div v-if="!loadingCategories && availableCategories.length === 0" class="text-center py-8">
                <svg class="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <p class="text-sm text-gray-500">No categories available</p>
              </div>
            </div>
          </div>

          <!-- Right Panel: Variables -->
          <div class="flex-1 flex flex-col">
            <!-- Search and Header -->
            <div class="p-6 border-b border-gray-200">
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-sm font-semibold text-gray-700">Variables</h3>
                <div v-if="localSelectedVariables.length > 0" class="text-xs text-gray-500">
                  {{ localSelectedVariables.length }} selected
                </div>
              </div>
              
              <div class="relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Search variables..."
                  class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                <svg class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
            </div>

            <!-- Variables List -->
            <div class="flex-1 overflow-y-auto">
              <!-- Loading state -->
              <div v-if="loadingVariables" class="flex items-center justify-center py-8">
                <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span class="ml-2 text-sm text-gray-500">Loading variables...</span>
              </div>
              
              <!-- Variables -->
              <div v-else-if="filteredVariables.length > 0" class="space-y-0">
                <div
                  v-for="variable in filteredVariables"
                  :key="variable.path"
                  class="flex items-start space-x-3 px-6 py-4 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <input
                    type="checkbox"
                    :checked="isVariableSelected(variable)"
                    @change="toggleVariableSelection(variable, $event)"
                    class="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  >
                  <div class="flex-1 min-w-0">
                    <!-- Variable Label with Property Key -->
                    <div class="text-sm font-medium text-gray-900 mb-1">
                      {{ variable.label }}
                      <span class="text-xs text-gray-500 font-mono ml-2">({{ getPropertyKey(variable.path) }})</span>
                    </div>
                    
                    <!-- Description -->
                    <div v-if="variable.description" class="text-xs text-gray-600 mb-2 leading-relaxed">
                      {{ variable.description }}
                    </div>
                    
                    <!-- Type Badge -->
                    <div class="flex items-center gap-2">
                      <span
                        :class="[
                          'px-2 py-0.5 text-xs rounded-full font-medium',
                          getTypeColor(variable.type)
                        ]"
                      >
                        {{ formatVariableType(variable.type) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No variables message -->
              <div v-else-if="!loadingVariables && selectedCategory" class="p-8 text-center text-gray-500">
                <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0118 12M6 20.291A7.962 7.962 0 016 12m0 8.291zm12 0z"></path>
                </svg>
                <p class="text-sm">
                  {{ searchQuery ? 'No variables found matching your search.' : 'No variables available for this category.' }}
                </p>
              </div>

              <!-- Select category message -->
              <div v-else-if="!loadingVariables && !selectedCategory" class="p-8 text-center text-gray-500">
                <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                </svg>
                <p class="text-sm">Select a category to view available variables</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div class="text-sm text-gray-600">
            <span v-if="localSelectedVariables.length > 0">
              {{ localSelectedVariables.length }} variable{{ localSelectedVariables.length !== 1 ? 's' : '' }} selected
            </span>
            <span v-else>No variables selected</span>
          </div>
          
          <div class="flex items-center space-x-3">
            <button
              @click="closeModal"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Cancel
            </button>
            <button
              @click="saveAndClose"
              class="px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
            >
              Save & Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { agsStore } from '../../../composables/useAGSStore.js';

export default {
  name: 'AGSFilterDialog',
  
  props: {
    show: {
      type: Boolean,
      default: false
    },
    selectedVariables: {
      type: Array,
      default: () => []
    },
    currentZoom: {
      type: Number,
      default: 4
    }
  },
  
  data() {
    return {
      selectedCategory: null,
      searchQuery: '',
      localSelectedVariables: [...this.selectedVariables],
      
      loadingCategories: false,
      loadingVariables: false,
      
      availableCategories: [],
      allCategoryVariables: {}, // Store all variables for all categories
      
      // AGS Store instance
      agsStore: null
    }
  },
  
  created() {
    // Use singleton AGS store
    this.agsStore = agsStore;
    
    console.log('🏗️ AGS Dialog: Component created with props:', {
      selectedVariables: this.selectedVariables,
      show: this.show,
      agsStoreExists: !!this.agsStore,
      agsStoreVariables: this.agsStore ? this.agsStore.selectedVariables : 'N/A'
    });
  },
  
  computed: {
    filteredVariables() {
      if (!this.selectedCategory) {
        return [];
      }
      
      let variables = this.allCategoryVariables[this.selectedCategory.category_name] || [];
      
      // Filter by search query
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        variables = variables.filter(variable => 
          variable.label.toLowerCase().includes(query) ||
          variable.path.toLowerCase().includes(query) ||
          (variable.description && variable.description.toLowerCase().includes(query))
        );
      }
      
      return variables;
    }
  },
  
  watch: {
    selectedVariables: {
      handler(newValue) {
        console.log('👀 AGS Dialog: selectedVariables prop changed:', {
          newValue: newValue,
          count: newValue.length
        });
        this.localSelectedVariables = [...newValue];
        console.log('📝 AGS Dialog: localSelectedVariables updated:', this.localSelectedVariables);
      },
      deep: true,
      immediate: true
    },
    
    show(newValue) {
      if (newValue) {
        console.log('🔍 AGS Dialog: Modal opened with selectedVariables:', {
          selectedVariables: this.selectedVariables,
          count: this.selectedVariables.length
        });
        
        // Reset to defaults when modal opens
        this.searchQuery = '';
        this.localSelectedVariables = [...this.selectedVariables];
        
        console.log('📝 AGS Dialog: Initialized localSelectedVariables:', this.localSelectedVariables);
        
        // Load all categories and their variables at once
        this.loadAllCategoriesAndVariables();
      }
    }
  },
  
  methods: {
    closeModal() {
      this.$emit('close');
    },
    
    saveAndClose() {
      console.log('💾 Saving AGS variables:', {
        localSelectedVariables: this.localSelectedVariables,
        count: this.localSelectedVariables.length,
        agsStoreExists: !!this.agsStore
      });
      
      // Create plain object copies to avoid reactivity issues
      const plainVariables = this.localSelectedVariables.map(variable => 
        JSON.parse(JSON.stringify({
          path: variable.path,
          label: variable.label,
          description: variable.description,
          type: variable.type,
          category_name: variable.category_name,
          category_id: variable.category_id
        }))
      );
      
      console.log('💾 Plain variables to save:', plainVariables);
      
      // Update the AGS store with selected variables
      if (this.agsStore) {
        this.agsStore.setSelectedVariables(plainVariables);
        console.log('🏪 AGS Store updated with variables:', this.agsStore.selectedVariables);
      }
      
      this.$emit('update:selectedVariables', plainVariables);
      this.$emit('save', plainVariables);
      
      console.log('📤 Emitted save event with variables:', this.localSelectedVariables);
      
      this.closeModal();
    },
    
    selectCategory(category) {
      this.selectedCategory = category;
    },
    
    formatGeographicLevel(level) {
      const levelMap = {
        'state': 'States',
        'zip': 'ZIP Codes',
        'tract': 'Census Tracts',
        'block_group': 'Block Groups',
        'block': 'Blocks'
      };
      return levelMap[level] || level;
    },
    
    async loadAllCategoriesAndVariables() {
      this.loadingCategories = true;
      this.loadingVariables = true;
      
      try {
        // Load all categories with their variables included
        const response = await this.fetchAgsCategories();
        this.availableCategories = response.categories || [];
        
        console.log(`Loaded ${this.availableCategories.length} AGS categories`);
        
        // Extract variables from each category (they're now included in the API response)
        this.allCategoryVariables = {};
        
        this.availableCategories.forEach(category => {
          // Extract variables from the variable_schema
          const variables = this.extractVariablesFromSchema(category.variable_schema || {});
          
          // Add category info to each variable, including tier information
          const enrichedVariables = variables.map(variable => ({
            ...variable,
            category_name: category.category_name,
            category_id: category.id || category.category_name,
            data_tiers: category.data_tiers || ['base'] // Include tier info for dynamic tier selection
          }));
          
          this.allCategoryVariables[category.category_name] = enrichedVariables;
          console.log(`Loaded ${variables.length} variables for category: ${category.category_name}`);
        });
        
        console.log('All AGS categories and variables loaded successfully');
        
      } catch (error) {
        console.error('Error loading AGS data:', error);
        
        // Provide specific error messages based on error type
        let errorMessage = 'Failed to load AGS data. Please try again.';
        
        if (error.message?.includes('timeout')) {
          errorMessage = 'Request timed out while loading AGS data. Please try again.';
        } else if (error.message?.includes('Network error') || error.message?.includes('network')) {
          errorMessage = 'Network error loading AGS data. Please check your connection.';
        } else if (error.message?.includes('403')) {
          errorMessage = 'Access denied to AGS data. You may not have permission to view this data.';
        } else if (error.message?.includes('500')) {
          errorMessage = 'Server error loading AGS data. Please try again later.';
        }
        
        this.$toast?.error(errorMessage, {
          timeout: 6000,
          position: 'bottom-right'
        });
        
        this.availableCategories = [];
        this.allCategoryVariables = {};
      } finally {
        this.loadingCategories = false;
        this.loadingVariables = false;
      }
    },
    
    async fetchAgsCategories() {
      try {
        // Use the existing API endpoint with timeout (no geographic level filtering)
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const response = await fetch('/api/cresiteiq2/ags/categories', {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          let errorMessage = `HTTP error! status: ${response.status}`;
          
          // Try to get error details from response
          try {
            const errorData = await response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          } catch (e) {
            // Ignore JSON parsing errors, use default message
          }
          
          throw new Error(errorMessage);
        }
        
        return await response.json();
        
      } catch (error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timed out while loading AGS categories');
        }
        throw error;
      }
    },
    

    
    getTypeColor(type) {
      const colors = {
        'integer': 'bg-blue-100 text-blue-800',
        'number': 'bg-green-100 text-green-800',
        'string': 'bg-purple-100 text-purple-800',
        'boolean': 'bg-yellow-100 text-yellow-800',
        'array': 'bg-red-100 text-red-800',
        'object': 'bg-gray-100 text-gray-800'
      };
      
      return colors[type] || 'bg-gray-100 text-gray-800';
    },
    
    getPropertyKey(path) {
      // Extract the last part of the path as the property key
      // e.g., "properties.HHDCY" becomes "HHDCY"
      if (!path) return '';
      
      const parts = path.split('.');
      return parts[parts.length - 1];
    },
    
    formatVariableType(type) {
      // Format type names for better readability
      const typeMap = {
        'integer': 'Number',
        'number': 'Decimal',
        'float': 'Decimal',
        'string': 'Text',
        'boolean': 'Yes/No',
        'array': 'List',
        'object': 'Object'
      };
      
      return typeMap[type] || type;
    },
    
    extractVariablesFromSchema(schema) {
      const variables = [];
      
      // Handle the schema structure: { type: "object", properties: { ... } }
      if (schema.type === 'object' && schema.properties) {
        for (const [key, value] of Object.entries(schema.properties)) {
          if (value && typeof value === 'object' && value.type) {
            // This is a variable definition
            variables.push({
              path: key, // Use just the variable name directly
              label: value.description || this.formatLabel(key),
              type: value.type,
              description: value.description || null,
              unit: value.unit || null,
              vintage: value.vintage || null,
              ags_variable: value.ags_variable || key
            });
          }
        }
      }
      
      return variables;
    },
    
    formatLabel(key) {
      // Convert variable keys to readable format
      // For AGS variables, use the key as-is since they're already meaningful
      return key;
    },
    
    isVariableSelected(variable) {
      // Check if a variable is selected by comparing paths
      const isSelected = this.localSelectedVariables.some(selected => selected.path === variable.path);
      
      // Debug logging for the first few checks
      if (this.localSelectedVariables.length > 0 && Math.random() < 0.1) { // Log 10% of checks to avoid spam
        console.log('🔍 Checking if variable is selected:', {
          variablePath: variable.path,
          isSelected: isSelected,
          localSelectedCount: this.localSelectedVariables.length,
          localSelectedPaths: this.localSelectedVariables.map(v => v.path)
        });
      }
      
      return isSelected;
    },
    
    toggleVariableSelection(variable, event) {
      console.log('🔄 Variable selection toggled:', {
        variable: variable,
        checked: event.target.checked,
        currentSelected: this.localSelectedVariables.length
      });
      
      // Handle checkbox selection manually
      if (event.target.checked) {
        // Add variable if not already selected
        if (!this.isVariableSelected(variable)) {
          // Create a plain object copy to avoid Vue reactivity issues
          const plainVariable = JSON.parse(JSON.stringify({
            path: variable.path,
            label: variable.label,
            description: variable.description,
            type: variable.type,
            category_name: variable.category_name,
            category_id: variable.category_id
          }));
          this.localSelectedVariables.push(plainVariable);
          console.log('✅ Variable added:', plainVariable);
          console.log('📊 Total selected variables:', this.localSelectedVariables.length);
        } else {
          console.log('⚠️ Variable already selected:', variable.path);
        }
      } else {
        // Remove variable if selected
        const beforeCount = this.localSelectedVariables.length;
        this.localSelectedVariables = this.localSelectedVariables.filter(
          selected => selected.path !== variable.path
        );
        console.log('❌ Variable removed:', variable.path);
        console.log('📊 Variables count:', beforeCount, '->', this.localSelectedVariables.length);
      }
    }
  }
}
</script>

<style scoped>
/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Category card hover effects */
.cursor-pointer:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Badge spacing */
.flex.items-center.gap-1 {
  flex-wrap: wrap;
}

/* Variable list improvements */
.space-y-0 > div:last-child {
  border-bottom: none;
}

/* Type badge styling */
.rounded-full {
  border-radius: 9999px;
}

/* Variable description styling */
.leading-relaxed {
  line-height: 1.625;
}

/* Property key styling */
.font-mono {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace;
}

/* Transition animations */
.transition-colors {
  transition-property: color, background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

/* Loading spinner animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>